/**
 * The LLM model registry's shared shapes (docs/internal/architecture/
 * model-registry-and-admin-plan.md sections 3.3, 5.1 and 7; rewritten
 * 2026-10-06 when the registry became models, providers AND routes): the
 * column types of `llm_providers`, `llm_models`, `llm_model_routes`,
 * `llm_model_audit` and `llm_model_sync_runs` (migration 0095), the
 * disposition vocabulary every gate answers in, the skip reasons every
 * resolver, admin API and admin tab explain a dark route with, the Redis key
 * the cross-pod cooldowns live under, and the id rules the sync and the admin
 * API enforce.
 *
 * THE SHAPE. A MODEL is what a user picks and pays for: its LIST PRICE is the
 * retail basis (retail = list x MODEL_RETAIL_MULTIPLIER, computed at read
 * time, never stored). A PROVIDER is somebody the platform holds a credential
 * for, with two switches: `listed` (its home models may be offered) and
 * `routingEnabled` (its routes may serve). A ROUTE is one model on one
 * provider in that model's ordered fallback chain: the wire the provider
 * receives, the provider's own COST with a source and a checked date, an
 * enabled flag, the last probe. The user pays the model's retail whichever
 * route served; the route changes what the platform paid.
 *
 * ISOMORPHIC ON PURPOSE. `lib/db/schema.ts` imports these for its `$type<>()`
 * columns, the server module (`./registry.ts`) builds its snapshot from them,
 * `./registry-margin.ts` is pure and is rendered by the admin Models tab (a
 * `'use client'` component), so this file imports nothing but types from
 * `./catalog`, which is itself client safe. Server code (drizzle, settings,
 * Redis) lives in `./registry.ts` only.
 *
 * Why `AUTO_MODEL_ALIASES` is here: `lib/agents/factories.ts` keeps the
 * `auto/*` table as a module-private constant inside the agent runtime, and
 * the registry must answer "is `auto/smartest` offered?" with its TARGET's
 * disposition without loading the runtime. The copy is pinned to the
 * factory's table by `tests/unit/models/registry-dispositions.test.ts`; the
 * factory should import this one when that file is next edited.
 */
import type { ModelInfo, ModelPricing, ModelTransport } from './catalog';

// ---------------------------------------------------------------------------
// Closed vocabularies (each one is a CHECK constraint in migration 0095)
// ---------------------------------------------------------------------------

/** Where a model or route row came from: seeded from code, discovered on Bedrock, or hand-added by an admin. */
export type RegistrySource = 'catalog' | 'bedrock' | 'admin';
export const REGISTRY_SOURCES: readonly RegistrySource[] = ['catalog', 'bedrock', 'admin'];

/**
 * `pending`: discovered, not offered, not routable. `listed`: offered.
 * `delisted`: hidden and not newly pinnable; agents already pinned keep
 * running and billing at the model's retail. `retired`: hidden and routed to
 * the platform default with a warning; the stored config is never rewritten.
 */
export type RegistryStatus = 'pending' | 'listed' | 'delisted' | 'retired';
export const REGISTRY_STATUSES: readonly RegistryStatus[] = ['pending', 'listed', 'delisted', 'retired'];

/**
 * What a gate does with a model id. `offered` is the only disposition the
 * picker shows and the agent service accepts for a changed `model`; the
 * others say why not. `unknown` is an id neither the static catalog nor the
 * registry knows (a free-text write, a typo, a model the provider added that
 * nobody priced), which the rails refuse on a platform key while the registry
 * is on. While the registry is OFF every id is `offered`: that is today's
 * behaviour, exactly.
 */
export type ModelDisposition = 'offered' | 'delisted' | 'retired' | 'pending' | 'unknown';

export type RegistryTier = ModelInfo['tier'];
export const REGISTRY_TIERS: readonly RegistryTier[] = ['fastest', 'balanced', 'smartest'];
export const REGISTRY_TRANSPORTS: readonly ModelTransport[] = ['completions', 'realtime', 'local'];

/**
 * How a provider is called and credentialed. `direct`: an SDK adapter plus
 * an env key (`PROVIDER_ENV_KEYS`); `bedrock`: a token minted from the pod's
 * identity and the Bedrock adapters; `local`: the browser's own GPU, no key;
 * `mock`: the portal's echo route, a constant key.
 */
export type ProviderKind = 'direct' | 'bedrock' | 'local' | 'mock';
export const PROVIDER_KINDS: readonly ProviderKind[] = ['direct', 'bedrock', 'local', 'mock'];

/**
 * How a model's cached prompt tokens are billed to the USER on every route
 * (plan section 7). `discount`: the home provider's cache rate (the catalog's
 * `cacheReadPer1k`). `input`: the retail input rate, which keeps the price
 * route-independent and makes a route with no cache rate eligible. Set per
 * model by an admin; the seed writes `discount`.
 */
export type CacheBilling = 'discount' | 'input';
export const CACHE_BILLINGS: readonly CacheBilling[] = ['discount', 'input'];

/**
 * Where a route's `cost` came from. `catalog-seed`: the static `MODEL_PRICING`
 * row at seed; `bedrock-seed`: `BEDROCK_ROUTES` basis points of the catalog
 * price at seed; `published`: an admin copied the provider's price page; the
 * three `pricelist:*` parsers of plan section 8.3, in the order the sync
 * tries them; `admin`: an admin's number on a synced route, which the sync
 * then stops touching. A cost with no source, or no checked date, is no cost.
 */
export type RouteCostSource =
  | 'catalog-seed' | 'bedrock-seed' | 'published'
  | 'pricelist:mantle-usagetype' | 'pricelist:model-attr' | 'pricelist:marketplace-alias'
  | 'admin';
export const ROUTE_COST_SOURCES: readonly RouteCostSource[] = [
  'catalog-seed', 'bedrock-seed', 'published',
  'pricelist:mantle-usagetype', 'pricelist:model-attr', 'pricelist:marketplace-alias',
  'admin',
];

/** Where a model's `list_price` came from: the seed, a route copied at approval (`route:bedrock`), or an admin. Not a CHECK: the route half is open. */
export type ListPriceSource = 'catalog-seed' | `route:${string}` | 'admin';

/**
 * Why a route did not serve a call, in the order the resolver checks them
 * (plan section 5.3). The same words are counted in `llm_route_total`,
 * returned by the admin API and rendered by the Models tab, so the three
 * never disagree about why a route is dark.
 */
export type SkipReason =
  | 'disabled' | 'provider_routing_off' | 'env' | 'cost_unconfirmed' | 'margin'
  | 'no_credentials' | 'cooldown' | 'tools';
export const SKIP_REASONS: readonly SkipReason[] = [
  'disabled', 'provider_routing_off', 'env', 'cost_unconfirmed', 'margin', 'no_credentials', 'cooldown', 'tools',
];

/**
 * The Redis key one (model, provider) cooldown lives under (plan section
 * 5.4): `SET llm:route-cooldown:<model>:<provider> <untilMs> PX <ttl>`,
 * written through by lane B's resolver, read by the snapshot refresh and by
 * the admin API. Defined here so the admin API reads the same keys without
 * importing the routing module. `parse` splits on the LAST colon because a
 * model id may carry one (`anthropic.claude-haiku-4-5-20251001-v1:0` is a
 * wire id, never a model id, but the rule costs nothing).
 */
export const ROUTE_COOLDOWN_KEY = {
  prefix: 'llm:route-cooldown:',
  for(modelId: string, provider: string): string {
    return `${this.prefix}${modelId}:${provider}`;
  },
  parse(key: string): { modelId: string; provider: string } | null {
    if (!key.startsWith(this.prefix)) return null;
    const rest = key.slice(this.prefix.length);
    const at = rest.lastIndexOf(':');
    if (at <= 0 || at === rest.length - 1) return null;
    return { modelId: rest.slice(0, at), provider: rest.slice(at + 1) };
  },
} as const;

// ---------------------------------------------------------------------------
// JSON column shapes
// ---------------------------------------------------------------------------

/**
 * `ModelInfo.capabilities`, every field optional while a row is `pending`:
 * no API supplies `contextWindow`, `maxOutputTokens`, `thinking` or `vision`;
 * the probe fills `toolCalling` and an admin fills the rest before approval.
 * `rowToModelInfo` below is the one place that decides whether a row is
 * complete enough to be a `ModelInfo`.
 */
export type RegistryCapabilities = Partial<ModelInfo['capabilities']>;

/** The cross-Region inference-profile prefixes Bedrock knows (`BedrockProfile` in lib/llm/bedrock/config.ts, copied so this file stays import-free). */
export type BedrockProfilePrefix = 'global' | 'us' | 'eu' | 'au' | 'jp';
export const BEDROCK_PROFILE_PREFIXES: readonly BedrockProfilePrefix[] = ['global', 'us', 'eu', 'au', 'jp'];

/** The adapter family a Bedrock route is built with: the maker's parser for a routed maker model, `native` for a `bedrock/*` model. */
export type BedrockRouteAdapter = 'anthropic' | 'openai' | 'xai' | 'fireworks' | 'native';
export const BEDROCK_ROUTE_ADAPTERS: readonly BedrockRouteAdapter[] = ['anthropic', 'openai', 'xai', 'fireworks', 'native'];

/** A route on a `direct`, `local` or `mock` provider: the bare id the provider receives (today derived from the catalog id, now data). */
export interface DirectRouteWire {
  providerModelId: string;
  /** The provider API the id is sent to, when the adapter has more than one. Omitted: the adapter's default. */
  api?: 'messages' | 'responses' | 'chat' | 'gemini';
}

/**
 * A route on the `bedrock` provider: the union of `BedrockRouteTarget`
 * (lib/llm/bedrock/models.ts) and `BedrockNativeModel`
 * (lib/llm/bedrock/native-models.ts). `bedrockId` is WITHOUT any profile
 * prefix; `profile` says one is required; `profilePrefix` null means the
 * environment's `BEDROCK_PROFILE`. `basePath` omitted means the adapter's
 * default (`/openai/v1` for the OpenAI-compatible wires).
 */
export interface BedrockRouteWire {
  bedrockId: string;
  endpoint: 'runtime' | 'mantle';
  basePath?: '/openai/v1' | '/v1';
  profile: boolean;
  profilePrefix?: BedrockProfilePrefix | null;
  reasoningEffort?: boolean;
  adapter: BedrockRouteAdapter;
  /**
   * Bedrock-first build contract (2026-10-06): the Bedrock API beyond the
   * family default. Validated with the family and endpoint by
   * `bedrockWireApiProblem` (`invoke`: anthropic on runtime; `converse`:
   * fireworks or native on runtime).
   */
  api?: BedrockRouteApi;
  /** Converse rows: what the model supports there. */
  converse?: { toolCalling?: boolean; maxOutputTokens?: number; reasoning?: 'none' | 'always' };
}

/** Bedrock APIs a route may name beyond the family's default wire. */
export type BedrockRouteApi = 'invoke' | 'converse';
export const BEDROCK_ROUTE_APIS: readonly BedrockRouteApi[] = ['invoke', 'converse'];

/**
 * Why a stored Bedrock wire's API cannot be sent, or null (2026-10-06, the
 * Bedrock-first build; owner: "add missing apis"). The adapter key is
 * derived from (family, api) (lib/llm/bedrock/adapters.ts
 * `bedrockAdapterKey`), and only these combinations have an adapter:
 *   - `invoke`: InvokeModel with the Anthropic body, so the `anthropic`
 *     family only, on `bedrock-runtime` (mantle has no InvokeModel);
 *   - `converse`: the `fireworks` or `native` family only (Claude goes on
 *     Invoke, GPT and Grok have their own APIs on Bedrock), on
 *     `bedrock-runtime` (mantle has no Converse).
 * Converse capabilities (`converse`) on a wire that is not Converse are a
 * mistyped wire too: they would be silently ignored. `routeWire` refuses a
 * wire this names; lib/llm/routing/providers.ts `registryRouteProblem`
 * reports the same words, so the admin sees what the resolver darkens.
 * Takes the raw stored value: it is admin input, read as hostile.
 */
export function bedrockWireApiProblem(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null;
  const w = raw as Record<string, unknown>;
  const api = w.api;
  if (api !== undefined && api !== null && !(BEDROCK_ROUTE_APIS as readonly unknown[]).includes(api)) {
    return `names an unknown Bedrock API (${String(api).slice(0, 40)})`;
  }
  if (api === 'invoke') {
    if (w.adapter !== 'anthropic') return 'names the Invoke API, which only Claude models (the anthropic family) use';
    if (w.endpoint !== 'runtime') return 'names the Invoke API, which bedrock-runtime serves and mantle does not';
  }
  if (api === 'converse') {
    if (w.adapter !== 'fireworks' && w.adapter !== 'native') return 'names the Converse API, which only the fireworks and native families use';
    if (w.endpoint !== 'runtime') return 'names the Converse API, which bedrock-runtime serves and mantle does not';
  }
  const caps = w.converse;
  if (caps !== undefined && caps !== null) {
    if (api !== 'converse') return 'carries Converse capabilities on a wire that is not on the Converse API';
    if (typeof caps !== 'object' || Array.isArray(caps)) return 'carries Converse capabilities that are not an object';
    const c = caps as Record<string, unknown>;
    if (c.toolCalling !== undefined && typeof c.toolCalling !== 'boolean') return 'carries a Converse toolCalling that is not true or false';
    if (c.maxOutputTokens !== undefined && !(Number.isInteger(c.maxOutputTokens) && (c.maxOutputTokens as number) > 0)) {
      return 'carries a Converse maxOutputTokens that is not a positive integer';
    }
    if (c.reasoning !== undefined && c.reasoning !== 'none' && c.reasoning !== 'always') return 'carries a Converse reasoning that is not none or always';
  }
  return null;
}

export type RouteWire = DirectRouteWire | BedrockRouteWire;

/** A provider model id as it may appear in a request body. Checked before any use, with `isProviderModelId`. */
export const PROVIDER_MODEL_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,120}$/;

/**
 * A stored direct-wire id the adapters may send: `PROVIDER_MODEL_ID_RE` (the
 * plan's character set, which allows `/` for ids like a Fireworks account
 * path) and no empty, `.` or `..` path segment. The Gemini adapter builds
 * its URL path from the model id (webagents adapters/google.ts, today from
 * the last `/` segment only), so an id must never be able to act as a path
 * that walks to another endpoint on the provider's host with the platform
 * key, whatever a later adapter does with it. Only an admin writes a wire
 * (lane C, which validates with `routeWire`), so this is hardening rather
 * than an open hole (review, 2026-10-06).
 */
export function isProviderModelId(id: unknown): id is string {
  if (typeof id !== 'string' || !PROVIDER_MODEL_ID_RE.test(id)) return false;
  return id.split('/').every((seg) => seg !== '' && seg !== '.' && seg !== '..');
}

/** A Bedrock model id as it may appear in a URL, an ARN or a slug. Checked before any use. */
export const BEDROCK_MODEL_ID_RE = /^[a-z0-9][a-z0-9._:-]{1,120}$/;

/** What the live probe (plan section 8.4) learned about a ROUTE, for one `costVersion`. */
export interface RegistryProbe {
  /** ISO time of the probe. */
  at: string;
  ok: boolean;
  answered?: boolean;
  streamed?: boolean;
  usage?: boolean;
  tools?: boolean;
  reasoningEffort?: boolean;
  /** The cache test: the same long prompt twice; whether the second answer reported cached tokens. */
  cachedTokens?: boolean;
  endpoint?: BedrockRouteWire['endpoint'];
  basePath?: BedrockRouteWire['basePath'];
  costVersion?: string | null;
  error?: string | null;
}

/** Bedrock's `modelLifecycle`, as discovered. */
export interface RegistryLifecycle {
  status?: 'ACTIVE' | 'LEGACY' | string;
  endOfLifeTime?: string | null;
  publicExtendedAccessTime?: string | null;
}

/** A trimmed `FoundationModelSummary` plus the admin's Marketplace alias and the model card's hints. */
export interface RegistryDiscovery {
  name?: string;
  provider?: string;
  inputModalities?: string[];
  outputModalities?: string[];
  streaming?: boolean;
  inferenceTypes?: string[];
  /** Admin-confirmed `servicename` in the Marketplace price file. Never set automatically. */
  marketplaceName?: string | null;
  /** Parsed from the model card's markdown, suggestions only. */
  cardHints?: { contextWindow?: number; maxOutputTokens?: number; source?: string };
  /** The control plane answered 401/403 on the last run (no IAM yet): recorded, never silent. */
  forbidden?: boolean;
}

// ---------------------------------------------------------------------------
// Rows, as drizzle selects them (timestamps are `Date`; the admin API serialises them)
// ---------------------------------------------------------------------------

/** One row of `llm_providers`. `key` is the catalog provider key and the id prefix, never renamed (wire value). */
export interface ProviderRow {
  key: string;
  label: string;
  kind: ProviderKind;
  /** The env var name (`OPENAI_API_KEY`), `minted`, or `none`. Whether the key is PRESENT is read live, never stored. */
  keySource: string | null;
  /** This provider's HOME models may be offered. */
  listed: boolean;
  /** Its routes may serve, for any model. Off is "stop sending anything to X" without touching chains. */
  routingEnabled: boolean;
  /** Null means the adapter's default. */
  baseUrl: string | null;
  region: string | null;
  note: string | null;
  updatedAt: Date;
  updatedBy: string | null;
}

/** One row of `llm_models`. The TS property for the `approved_by` column is `approvedByUserId` (the bare name is reserved for inbox reply approvals, guarded by test). */
export interface RegistryRow {
  modelId: string;
  /** The HOME provider: the id prefix, the adapter family and the tool semantics the model keeps on every route. */
  provider: string;
  source: RegistrySource;
  status: RegistryStatus;
  label: string | null;
  tier: RegistryTier | null;
  transport: ModelTransport;
  capabilities: RegistryCapabilities | null;
  /** The retail basis, USD per 1K as decimal strings (the `MODEL_PRICING` shape). Retail is never stored. */
  listPrice: ModelPricing | null;
  listPriceSource: ListPriceSource | null;
  /** Required for the list price to be CONFIRMED. */
  listPriceCheckedAt: Date | null;
  cacheBilling: CacheBilling;
  lifecycle: RegistryLifecycle | null;
  discovery: RegistryDiscovery | null;
  missingSyncs: number;
  firstSeenAt: Date;
  lastSeenAt: Date;
  approvedAt: Date | null;
  approvedByUserId: string | null;
  updatedAt: Date;
  updatedBy: string | null;
}

/** One row of `llm_model_routes`: one model on one provider, at one place in the model's chain. Never deleted; `enabled = false` is the off switch. */
export interface RouteRow {
  id: string;
  modelId: string;
  /** The SERVING provider (`bedrock` for a routed maker model). */
  provider: string;
  /** 1 is tried first. The admin write renumbers a model's routes 1..n. */
  priority: number;
  enabled: boolean;
  wire: RouteWire;
  /** The provider's price, USD per 1K as decimal strings. `longContext` present means tiered; a missing cache leg means billed at the input rate. */
  cost: ModelPricing | null;
  costSource: RouteCostSource | null;
  /** The price file version the cost came from. */
  costVersion: string | null;
  /** Required for the cost to be CONFIRMED. The seed stamps it; the admin edit sets it; the sync sets it on every run that saw the price. */
  costCheckedAt: Date | null;
  costNote: string | null;
  /** null unknown (counts as "may report", plan decision 17.4), true, false (set by the probe; the settle flips it back to null on a surprise). */
  reportsCachedTokens: boolean | null;
  probe: RegistryProbe | null;
  source: RegistrySource;
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string | null;
}

/** `llm_model_audit.action`. The sync writes the first five; admins write the rest. */
export type RegistryAuditAction =
  | 'discovered' | 'price_changed' | 'retired' | 'probe' | 'reconciled'
  | 'approved' | 'listed' | 'delisted' | 'list_price_set' | 'cache_billing_set' | 'fields_set'
  | 'route_added' | 'route_enabled' | 'route_disabled' | 'route_cost_set' | 'route_cost_cleared'
  | 'route_wire_set' | 'chain_reordered'
  | 'provider_listed' | 'provider_delisted' | 'provider_routing_set'
  | 'seeded';

export type RegistrySyncTrigger = 'schedule' | 'admin' | 'cli';
export const REGISTRY_SYNC_TRIGGERS: readonly RegistrySyncTrigger[] = ['schedule', 'admin', 'cli'];
export type RegistrySyncOutcome = 'ok' | 'partial' | 'failed';
export const REGISTRY_SYNC_OUTCOMES: readonly RegistrySyncOutcome[] = ['ok', 'partial', 'failed'];

/** `llm_model_sync_runs.counts`. */
export interface RegistrySyncCounts {
  reconciled?: number;
  discovered?: number;
  new?: number;
  priced?: number;
  unpriced?: number;
  probed?: number;
  retired?: number;
  priceChanges?: number;
}

/** The three public price files' `version` fields, keyed by service code. */
export type RegistryPriceVersions = Record<string, string>;

/**
 * A discovered row's catalog id: `bedrock/` plus a slug of the Bedrock id
 * without its maker prefix. Approval refuses anything else.
 */
export const REGISTRY_MODEL_ID_RE = /^bedrock\/[a-z0-9][a-z0-9.-]{1,60}$/;

/**
 * The `auto/*` aliases and their targets. A copy of `AUTO_ALIASES` in
 * lib/agents/factories.ts (see the file header for why), pinned by test.
 */
export const AUTO_MODEL_ALIASES: Readonly<Record<string, string>> = {
  'auto/fastest': 'openai/gpt-6-luna',
  'auto/balanced': 'openai/gpt-6-luna',
  'auto/smartest': 'anthropic/claude-opus-5-5',
};

/** Redis channel every pod subscribes to for registry invalidation. */
export const MODEL_REGISTRY_INVALIDATE_CHANNEL = 'model-registry:invalidate';

/**
 * `GET /api/models?pinned=<id>` echoes a pinned model that is not among the
 * offered ones as this, so the picker can show the stored value with its
 * disposition instead of silently selecting its first option. `id` is the
 * stored string exactly (the option's value must equal it); the `ModelInfo`
 * fields are present when the catalog or the registry knows the model.
 */
export type PinnedModel = Partial<ModelInfo> & { id: string; label: string; disposition: ModelDisposition };

/** The `GET /api/models` body. */
export interface ModelsResponse {
  providers: Record<string, { label: string; models: ModelInfo[] }>;
  toolSupport: Record<string, Record<string, boolean>>;
  pinned?: PinnedModel;
}

// ---------------------------------------------------------------------------
// Pure helpers over the rows
// ---------------------------------------------------------------------------

function isThinking(v: unknown): v is ModelInfo['capabilities']['thinking'] {
  if (!v || typeof v !== 'object') return false;
  const t = v as Record<string, unknown>;
  return typeof t.supported === 'boolean' && Array.isArray(t.levels) && typeof t.default === 'string';
}

/**
 * A row as a `ModelInfo`, or null when it is not complete enough to be one:
 * a label, a tier, and the capabilities an admin must confirm before
 * approval (`contextWindow`, `maxOutputTokens`, `thinking`, the booleans).
 * The admin API runs the same check at approval; the catalog overlay runs it
 * at read time so a half-filled `pending` row never reaches a picker or a
 * lock estimate as if it were whole. Pure, so the admin tab can preview it.
 */
export function rowToModelInfo(row: RegistryRow): ModelInfo | null {
  const c = row.capabilities;
  if (!row.label || !row.tier || !c) return null;
  if (typeof c.contextWindow !== 'number' || !(c.contextWindow > 0)) return null;
  if (typeof c.maxOutputTokens !== 'number' || !(c.maxOutputTokens > 0)) return null;
  if (!isThinking(c.thinking)) return null;
  return {
    id: row.modelId,
    label: row.label,
    tier: row.tier,
    transport: row.transport,
    capabilities: {
      toolCalling: c.toolCalling === true,
      vision: c.vision === true,
      imageGeneration: c.imageGeneration === true,
      webSearch: c.webSearch === true,
      codeExecution: c.codeExecution === true,
      contextWindow: c.contextWindow,
      maxOutputTokens: c.maxOutputTokens,
      thinking: c.thinking,
    },
  };
}

const positiveLeg = (v: unknown): boolean => typeof v === 'string' && Number.isFinite(parseFloat(v)) && parseFloat(v) > 0;
const optionalLeg = (v: unknown): boolean => v === undefined || v === null || (typeof v === 'string' && Number.isFinite(parseFloat(v)) && parseFloat(v) >= 0);

/** A `Date`, an ISO string or epoch ms that parses to a real time. */
function isCheckedDate(at: unknown): boolean {
  if (at instanceof Date) return Number.isFinite(at.getTime());
  if (typeof at === 'string' || typeof at === 'number') return Number.isFinite(new Date(at).getTime());
  return false;
}

/**
 * A price is WELL FORMED when both the input and the output leg are present,
 * parseable and above zero, every optional leg that is present is a finite
 * non-negative rate, and a tier (when present) has a positive threshold and
 * positive legs. Numbers are hostile input on every admin path; this is the
 * one check they all share.
 */
export function isWellFormedPricing(p: unknown): p is ModelPricing {
  if (!p || typeof p !== 'object') return false;
  const r = p as Record<string, unknown>;
  if (!positiveLeg(r.inputPer1k) || !positiveLeg(r.outputPer1k)) return false;
  for (const k of ['cacheReadPer1k', 'cacheWritePer1k', 'audioInputPer1k', 'audioOutputPer1k', 'audioCacheReadPer1k']) {
    if (!optionalLeg(r[k])) return false;
  }
  const tier = r.longContext;
  if (tier !== undefined && tier !== null) {
    if (typeof tier !== 'object') return false;
    const t = tier as Record<string, unknown>;
    if (typeof t.thresholdTokens !== 'number' || !(t.thresholdTokens > 0)) return false;
    if (!positiveLeg(t.inputPer1k) || !positiveLeg(t.outputPer1k)) return false;
    if (!optionalLeg(t.cacheReadPer1k) || !optionalLeg(t.cacheWritePer1k)) return false;
  }
  return true;
}

/**
 * A price is CONFIRMED (plan section 7, fail-closed on price) when it is
 * well formed AND carries a checked date. A route with an unconfirmed cost
 * is `cost_unconfirmed` and dark; a model with an unconfirmed list price
 * falls to the static catalog's price.
 */
export function isConfirmedPricing(p: unknown, checkedAt: Date | string | number | null | undefined): p is ModelPricing {
  return isCheckedDate(checkedAt) && isWellFormedPricing(p);
}

/** A route's confirmed cost, or null (unconfirmed: no cost, a bad leg, or no checked date). */
export function routeCost(route: Pick<RouteRow, 'cost' | 'costCheckedAt'>): ModelPricing | null {
  return isConfirmedPricing(route.cost, route.costCheckedAt) ? route.cost : null;
}

/** Whether a stored wire is the Bedrock shape (`bedrockId`) rather than the direct one (`providerModelId`). */
export function isBedrockWire(w: RouteWire | null | undefined): w is BedrockRouteWire {
  return !!w && typeof (w as BedrockRouteWire).bedrockId === 'string';
}

/**
 * A route's wire, validated, or null when the stored shape is not one a
 * provider can be called with. The wire id comes only from here and never
 * from a request string; a malformed stored wire is no wire, and the rail
 * then reports "No API key available" rather than sending a guessed id.
 */
export function routeWire(route: Pick<RouteRow, 'wire'>): RouteWire | null {
  const w = route.wire as unknown;
  if (!w || typeof w !== 'object') return null;
  const r = w as Record<string, unknown>;
  if (typeof r.bedrockId === 'string') {
    if (!BEDROCK_MODEL_ID_RE.test(r.bedrockId)) return null;
    if (r.endpoint !== 'runtime' && r.endpoint !== 'mantle') return null;
    if (r.basePath !== undefined && r.basePath !== null && r.basePath !== '/openai/v1' && r.basePath !== '/v1') return null;
    if (typeof r.profile !== 'boolean') return null;
    if (r.profilePrefix !== undefined && r.profilePrefix !== null && !(BEDROCK_PROFILE_PREFIXES as readonly unknown[]).includes(r.profilePrefix)) return null;
    if (!(BEDROCK_ROUTE_ADAPTERS as readonly unknown[]).includes(r.adapter)) return null;
    // The API and its capabilities (2026-10-06): a combination no adapter
    // serves is no wire at all, never a guess (`bedrockWireApiProblem`).
    if (bedrockWireApiProblem(r)) return null;
    const out: BedrockRouteWire = {
      bedrockId: r.bedrockId,
      endpoint: r.endpoint,
      profile: r.profile,
      adapter: r.adapter as BedrockRouteAdapter,
      reasoningEffort: r.reasoningEffort === true,
    };
    if (r.basePath === '/openai/v1' || r.basePath === '/v1') out.basePath = r.basePath;
    if (r.profilePrefix) out.profilePrefix = r.profilePrefix as BedrockProfilePrefix;
    if (r.api === 'invoke' || r.api === 'converse') out.api = r.api;
    if (r.api === 'converse' && r.converse && typeof r.converse === 'object') {
      const c = r.converse as Record<string, unknown>;
      const caps: NonNullable<BedrockRouteWire['converse']> = {};
      if (typeof c.toolCalling === 'boolean') caps.toolCalling = c.toolCalling;
      if (typeof c.maxOutputTokens === 'number') caps.maxOutputTokens = c.maxOutputTokens;
      if (c.reasoning === 'none' || c.reasoning === 'always') caps.reasoning = c.reasoning;
      out.converse = caps;
    }
    return out;
  }
  if (typeof r.providerModelId === 'string') {
    if (!isProviderModelId(r.providerModelId)) return null;
    const out: DirectRouteWire = { providerModelId: r.providerModelId };
    if (r.api === 'messages' || r.api === 'responses' || r.api === 'chat' || r.api === 'gemini') out.api = r.api;
    return out;
  }
  return null;
}

/** The id the serving provider receives as `model`, from a validated wire (profile-prefixed where the Bedrock wire needs one). */
export function wireModelId(wire: RouteWire, defaultProfilePrefix: BedrockProfilePrefix = 'global'): string {
  if (isBedrockWire(wire)) return wire.profile ? `${wire.profilePrefix ?? defaultProfilePrefix}.${wire.bedrockId}` : wire.bedrockId;
  return wire.providerModelId;
}
